import React from 'react';
import PropTypes from 'prop-types';
import { Resizable } from 're-resizable';
import { DraggableCore } from 'react-draggable';
import ReactTooltip from 'react-tooltip';
import classNames from 'classnames';
import bowser from 'yola-bowser';
import Icon from '../icon';
import noop from '../utils/noop';
import withUnit from '../utils/with-unit';
import makeEven from '../utils/make-even';
import preventBodyScroll from '../utils/ios-workarounds/prevent-body-scroll';
import withPrompt from './with-prompt';
import normalizeOffset from './helpers/normalize-offset';

const RESIZE_TRIGGER_SELECTOR = '.re-resizable-handle';
const OVERLAY_CLICK_RESTRICTED_SELECTOR = '.ws-ui-popover-content';
const DRAG_ELEMENT_CLASS = 'ws-modal--drag-element';

class Modal extends React.Component {
  constructor(props) {
    super(props);

    const { width, height, offsetX, offsetY, modalContentRef, fullscreen } = props;

    this.state = {
      isFullscreen: false,
      isResizing: false,
      isDragging: false,
      offsetX,
      offsetY,
      width,
      height,
      offset: {
        x: 0,
        y: 0,
      },
      modal: {
        width: 0,
        height: 0,
      },
      overlay: {
        height: 0,
        width: 0,
      },
      originalWidth: 0,
      originalHeight: 0,
    };

    this.containerRef = React.createRef();
    this.modalRef = fullscreen && modalContentRef ? modalContentRef : React.createRef();

    this.bindMethods();
  }

  componentDidMount() {
    const { isBodyScrollDisabled } = this.props;

    window.addEventListener('resize', this.onWindowResize, false);
    window.addEventListener('orientationchange', this.onWindowResize, false);
    document.addEventListener('keydown', this.handleKeyDown, false);
    document.addEventListener('mousedown', this.handleDocumentMouseEvent, false);
    this.modalRef.current.addEventListener('mousedown', this.handleModalContentClick, false);

    if (isBodyScrollDisabled) {
      this.applyDisableBodyScrolling();
    }

    if (bowser.ios) {
      this.applyIOSWorkarounds();
    }

    this.calculateCoords({
      onCalculate: this.onReady,
    });
    this.addGrabCursor();
    ReactTooltip.rebuild();
  }

  componentDidUpdate(prevProps) {
    const metrics = this.getBasicMetrics(prevProps);

    this.calculateCoords({
      metrics,
    });
    this.addGrabCursor();
  }

  componentWillUnmount() {
    const { isBodyScrollDisabled } = this.props;

    window.removeEventListener('resize', this.onWindowResize, false);
    window.removeEventListener('orientationchange', this.onWindowResize, false);
    document.removeEventListener('keydown', this.handleKeyDown, false);
    document.removeEventListener('mousedown', this.handleDocumentMouseEvent, false);
    this.modalRef.current.removeEventListener('mousedown', this.handleModalContentClick, false);

    if (isBodyScrollDisabled) {
      this.rollbackDisableBodyScrolling();
    }

    if (bowser.ios) {
      this.rollbackIOSWorkarounds();
    }
  }

  onReady() {
    const { modal, overlay } = this.state;
    const { onReady, centered, offsetX, offsetY } = this.props;
    if (!centered) {
      return onReady();
    }

    return this.setState(
      {
        offsetX: (overlay.width - modal.width) / 2 + offsetX,
        offsetY: (overlay.height - modal.height) / 2 + offsetY,
      },
      onReady
    );
  }

  onResizeStart() {
    const { modal } = this.state;
    this.setState({
      isResizing: true,
      originalWidth: modal.width,
      originalHeight: modal.height,
    });
  }

  onWindowResize() {
    const { onResize, fullscreen } = this.props;

    if (fullscreen) {
      onResize();
    }

    this.calculateCoords();
  }

  onResize(event, dir, ref, size) {
    const { originalWidth, originalHeight } = this.state;

    this.setState({
      width: originalWidth + size.width,
      height: originalHeight + size.height,
    });
  }

  onResizeStop() {
    this.setState({
      isResizing: false,
      originalWidth: 0,
      originalHeight: 0,
    });
  }

  onDrag(event, { deltaX, deltaY }) {
    const { offsetX, offsetY } = this.state;
    const currentX = offsetX + deltaX;
    const currentY = offsetY + deltaY;
    this.setState({
      offsetX: currentX <= 0 ? 0 : currentX,
      offsetY: currentY <= 0 ? 0 : currentY,
    });
  }

  onDragEnd(event) {
    const { offsetX, offsetY } = this.state;
    this.setState({
      isDragging: false,
    });

    const { onDragEnd } = this.props;

    if (onDragEnd) {
      onDragEnd(event, { x: offsetX, y: offsetY });
    }
  }

  onDragStart() {
    this.setState({
      isDragging: true,
    });
  }

  onOverlayClick(e) {
    const node = this.modalRef.current;

    const { onOverlayClick, floatingModalElementSelector } = this.props;
    if (
      !node.contains(e.target) &&
      !e.target.closest(floatingModalElementSelector) &&
      !e.target.closest(OVERLAY_CLICK_RESTRICTED_SELECTOR)
    ) {
      onOverlayClick();
    }
  }

  getSize() {
    const { minHeight, minWidth } = this.props;
    const { width } = this.state;
    let { height } = this.state;

    height = height === 'auto' ? this.modalRef.current.offsetHeight : height;

    const overlay = this.getOverlaySize();
    let newWidth = width;
    let newHeight = height;

    if (newWidth <= minWidth) {
      newWidth = minWidth;
    } else if (newWidth >= overlay.width) {
      newWidth = overlay.width;
    } else {
      newWidth = makeEven(newWidth);
    }

    if (newHeight <= minHeight) {
      newHeight = minHeight;
    } else if (newHeight >= overlay.height) {
      newHeight = overlay.height;
    } else {
      newHeight = makeEven(newHeight);
    }

    return {
      modal: {
        width: newWidth,
        height: newHeight,
      },
      overlay,
    };
  }

  getOverlaySize() {
    const { overlaySpacing, fullscreen } = this.props;
    const overlay = this.containerRef.current;
    const { height, width } = overlay.getBoundingClientRect();

    return {
      height: !fullscreen && overlaySpacing.y ? height - overlaySpacing.y * 2 : height,
      width: !fullscreen && overlaySpacing.x ? width - overlaySpacing.x * 2 : width,
    };
  }

  getModalCoords(metrics) {
    const { modal, overlay } = this.getSize();
    const offset = this.getOffset({ modal, overlay, metrics });

    return {
      modal,
      overlay,
      offset,
    };
  }

  getBasicMetrics(prevProps) {
    const { offsetX: currentOffsetX, offsetY: currentOffsetY } = this.state;
    const { offsetX: passedOffsetX, offsetY: passedOffsetY } = this.props;
    const { offsetX, offsetY } = prevProps;

    return {
      offsetX: offsetX !== passedOffsetX ? passedOffsetX : currentOffsetX,
      offsetY: offsetY !== passedOffsetY ? passedOffsetY : currentOffsetY,
    };
  }

  getOffset({ modal, overlay, metrics }) {
    const { centered } = this.props;

    if (centered) {
      return {
        x: (overlay.width - modal.width) / 2,
        y: (overlay.height - modal.height) / 2,
      };
    }

    const { offsetX } = metrics || this.state;
    const { offsetY } = metrics || this.state;

    const x = normalizeOffset(offsetX, modal.width, overlay.width);
    const y = normalizeOffset(offsetY, modal.height, overlay.height);

    return { x, y };
  }

  bindMethods() {
    this.onResizeStart = this.onResizeStart.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
    this.onResizeStop = this.onResizeStop.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.onDrag = this.onDrag.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.calculateCoords = this.calculateCoords.bind(this);
    this.onReady = this.onReady.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleDocumentMouseEvent = this.handleDocumentMouseEvent.bind(this);
    this.handleModalContentClick = this.handleModalContentClick.bind(this);
    this.getBasicMetrics = this.getBasicMetrics.bind(this);
  }

  addGrabCursor() {
    const { draggable, fullscreen } = this.props;

    if (!draggable || fullscreen) {
      return;
    }

    const { dragHandleSelector } = this.props;
    const dragHandlers = this.containerRef.current.querySelectorAll(`${dragHandleSelector}`);

    [...dragHandlers].forEach((el) => el.classList.add(DRAG_ELEMENT_CLASS));
  }

  applyIOSWorkarounds() {
    this.ios = {
      preventBodyScroll: preventBodyScroll('.ws-modal'),
    };

    document.addEventListener('touchmove', this.ios.preventBodyScroll, { passive: true });
    document.addEventListener('touchstart', this.handleDocumentMouseEvent, false);
  }

  rollbackIOSWorkarounds() {
    document.removeEventListener('touchmove', this.ios.preventBodyScroll);
    document.removeEventListener('touchstart', this.handleDocumentMouseEvent, false);
  }

  applyDisableBodyScrolling() {
    this.windowScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${this.windowScrollY || '0'}px`;
  }

  rollbackDisableBodyScrolling() {
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    window.scrollTo(0, parseInt(this.windowScrollY || '0', 10));
  }

  calculateCoords({ metrics, onCalculate } = {}) {
    const { fullscreen } = this.props;

    if (fullscreen) {
      this.stretchOnFullscreen(onCalculate);

      return;
    }

    const newCoords = this.getModalCoords(metrics);

    const { modal, offset } = this.state;
    const { onResize } = this.props;

    const isSizeChanged =
      newCoords.modal.width !== modal.width || newCoords.modal.height !== modal.height;

    const isOffsetChanged = newCoords.offset.x !== offset.x || newCoords.offset.y !== offset.y;

    if (isSizeChanged || isOffsetChanged) {
      this.setState(
        {
          ...metrics,
          ...newCoords,
          isFullscreen: false,
        },
        () => {
          if (isSizeChanged) onResize();
          if (onCalculate) onCalculate();
        }
      );
    }
  }

  stretchOnFullscreen(onCalculate) {
    const { isFullscreen } = this.state;

    if (!isFullscreen) {
      this.setState(
        {
          offset: {
            x: 0,
            y: 0,
          },
          modal: {
            width: '100%',
            height: '100%',
          },
          isFullscreen: true,
        },
        onCalculate
      );
    }
  }

  makeResizable(content) {
    const { resizable, fullscreen } = this.props;

    if (!resizable || fullscreen) {
      return content;
    }

    const { modal, overlay, offset } = this.state;
    const { maxWidth, maxHeight, minWidth, minHeight } = this.props;
    const widthWithoutOffset = overlay.width - offset.x;
    const heightWithoutOffset = overlay.height - offset.y;

    const newMaxWidth = maxWidth && maxWidth < widthWithoutOffset ? maxWidth : widthWithoutOffset;
    const newMaxHeight =
      maxHeight && maxHeight < heightWithoutOffset ? maxHeight : heightWithoutOffset;

    return (
      <Resizable
        size={{
          width: parseInt(modal.width, 10),
          height: parseInt(modal.height, 10),
        }}
        minWidth={minWidth}
        minHeight={minHeight}
        onResizeStart={this.onResizeStart}
        onResize={this.onResize}
        onResizeStop={this.onResizeStop}
        maxWidth={newMaxWidth}
        maxHeight={newMaxHeight}
        handleWrapperClass={RESIZE_TRIGGER_SELECTOR}
        handleComponent={{
          bottomRight: (
            <div className="ws-modal__resize">
              <Icon glyph="nwse-resize" size="16" />
            </div>
          ),
        }}
        enable={{
          bottomRight: true,
        }}
      >
        {content}
      </Resizable>
    );
  }

  makeDraggable(content) {
    const { dragHandleSelector, dragCancelSelector, draggable, fullscreen, modalContentRef } =
      this.props;

    if (fullscreen) return content;

    const dragCancelSelectors = `${dragCancelSelector}, ${RESIZE_TRIGGER_SELECTOR}`;
    const { offset } = this.state;
    const position = {
      transform: `translate3d(${parseInt(offset.x, 10)}px, ${parseInt(offset.y, 10)}px, 0)`,
    };
    return (
      <DraggableCore
        bounds="parent"
        handle={dragHandleSelector}
        onStop={this.onDragEnd}
        onDrag={this.onDrag}
        onStart={this.onDragStart}
        cancel={dragCancelSelectors}
        disabled={!draggable}
      >
        <div style={position} ref={modalContentRef}>
          {content}
        </div>
      </DraggableCore>
    );
  }

  hasSquaredCorners() {
    const { modal, overlay } = this.state;

    return modal.height === overlay.height && modal.width === overlay.width;
  }

  handleKeyDown(e) {
    const { onKeyDown, onPressEsc } = this.props;
    onKeyDown(e);
    if (e.key === 'Escape') {
      onPressEsc();
    }
  }

  handleDocumentMouseEvent(e) {
    const { overlay } = this.props;

    if ((overlay === 'none' || overlay === 'transparent') && !this.isClickWithinModal) {
      this.onOverlayClick(e);
    }
    this.isClickWithinModal = false;
  }

  handleModalContentClick() {
    this.isClickWithinModal = true;
  }

  render() {
    const {
      children,
      overlay,
      className,
      containerClassName,
      fullscreen,
      fullscreenIndent,
      shouldDisablePointerEvents,
      attributes,
      overlaySpacing,
      isContainerScrollable,
    } = this.props;
    const { modal, isResizing, isDragging, height } = this.state;
    const modalClass = classNames('ws-modal', className, {
      'ws-modal--is-resizing': isResizing,
      'ws-modal--fullscreen': fullscreen || this.hasSquaredCorners(),
    });
    const modalContainerClass = classNames('ws-modal-container', containerClassName, {
      'ws-modal-container--events-disabled': isResizing || isDragging || shouldDisablePointerEvents,
      'ws-modal-container--overlay-visible': overlay === 'visible',
      'ws-modal-container--overlay-transparent': overlay === 'transparent',
      'ws-modal-container--overlay-none': overlay === 'none',
      'ws-modal-container--scrollable': isContainerScrollable && !fullscreen,
    });

    const modalWidth = withUnit(modal.width);
    const modalHeight = height === 'auto' && !fullscreen ? 'auto' : withUnit(modal.height);
    const onMouseDown = !bowser.ios && overlay === 'visible' ? this.onOverlayClick : null;
    const onTouchStart = bowser.ios && overlay === 'visible' ? this.onOverlayClick : null;

    const style = {
      ...(modal.width && { width: modalWidth }),
      height: modalHeight,
    };
    if (fullscreen && fullscreenIndent) {
      style.padding = fullscreenIndent;
      style.boxSizing = 'border-box';
    }

    const containerStyle = {
      ...(!fullscreen &&
        overlaySpacing.x && {
          paddingLeft: `${overlaySpacing.x}px`,
          paddingRight: `${overlaySpacing.x}px`,
        }),
      ...(!fullscreen &&
        overlaySpacing.y && {
          paddingTop: `${overlaySpacing.y}px`,
          paddingBottom: `${overlaySpacing.y}px`,
        }),
    };

    return (
      <div
        {...attributes}
        className={modalContainerClass}
        role="dialog"
        ref={this.containerRef}
        style={containerStyle}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
      >
        {this.makeDraggable(
          <div className={modalClass} ref={this.modalRef} style={style}>
            {this.makeResizable(children)}
          </div>
        )}
      </div>
    );
  }
}

Modal.propTypes = {
  attributes: PropTypes.shape(),
  width: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minWidth: PropTypes.number,
  minHeight: PropTypes.number,
  maxWidth: PropTypes.number,
  maxHeight: PropTypes.number,
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  overlaySpacing: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  isContainerScrollable: PropTypes.bool,
  fullscreenIndent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resizable: PropTypes.bool,
  draggable: PropTypes.bool,
  overlay: PropTypes.oneOf(['transparent', 'visible', 'none']),
  shouldDisablePointerEvents: PropTypes.bool,
  fullscreen: PropTypes.bool,
  dragHandleSelector: PropTypes.string,
  dragCancelSelector: PropTypes.string,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  centered: PropTypes.bool,
  children: PropTypes.node.isRequired,
  onOverlayClick: PropTypes.func,
  onResize: PropTypes.func,
  onReady: PropTypes.func,
  onPressEsc: PropTypes.func,
  onKeyDown: PropTypes.func,
  onDragEnd: PropTypes.func,
  modalContentRef: PropTypes.shape({
    current: PropTypes.node,
  }),
  floatingModalElementSelector: PropTypes.string,
  isBodyScrollDisabled: PropTypes.bool,
};

Modal.defaultProps = {
  attributes: {},
  minWidth: 280,
  minHeight: 160,
  maxWidth: 0,
  maxHeight: 0,
  width: 0,
  height: 0,
  offsetX: 0,
  offsetY: 0,
  overlaySpacing: {},
  isContainerScrollable: false,
  fullscreenIndent: 0,
  resizable: true,
  draggable: true,
  shouldDisablePointerEvents: false,
  dragHandleSelector: '.ws-modal',
  dragCancelSelector: '.ws-input, .ws-button',
  className: '',
  containerClassName: '',
  centered: false,
  overlay: 'transparent',
  fullscreen: false,
  onOverlayClick: noop,
  onResize: noop,
  onReady: noop,
  onDragEnd: noop,
  onKeyDown: noop,
  onPressEsc: noop,
  modalContentRef: null,
  floatingModalElementSelector: null,
  isBodyScrollDisabled: false,
};

export { Modal };
export default withPrompt(Modal);
